import {
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconStar } from '@tabler/icons-react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useRef } from 'react';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import { TermsAndConditionsDocument } from './TermsAndConditionsDocument';
import classes from './TermsAndConditionsSetRow.module.css';
import type {
  FeeField,
  RememberedFees,
  TermsAndConditionsFormValues,
} from './terms-and-conditions-form';
import { INPUT_SIZE, initialRememberedFees, nextFeeValue } from './terms-and-conditions-form';

type Props = {
  form: UseFormReturnType<TermsAndConditionsFormValues>;
  index: number;
  onSetAsDefault: () => void;
  canSetAsDefault: boolean;
  // Parked until deleting a set gets a confirmation step.
  // onDelete: () => void;
  // canDelete: boolean;
};

export const TermsAndConditionsSetRow = ({
  form,
  index,
  onSetAsDefault,
  canSetAsDefault,
}: Props) => {
  const { t } = useTranslation();
  const set = form.getValues().termsAndConditions[index];
  const path = `termsAndConditions.${index}` as const;
  const isPercentagePenalty = set.penaltyClause?.type === 'percentage';
  const currencySymbol = getCurrencySymbolFromCurrencyCode(useCompanyConfiguration().currency);
  const currencySectionWidth = currencySymbol.length > 1 ? 36 : 24;

  // Kept across renders so switching a fee off and back on restores the numbers
  // it held rather than resetting them.
  const rememberedFees = useRef<RememberedFees>(initialRememberedFees());

  const toggleFee = <K extends FeeField>(field: K, enabled: boolean) => {
    const { value, remembered } = nextFeeValue(
      rememberedFees.current,
      field,
      set[field] as RememberedFees[K] | null,
      enabled,
    );
    rememberedFees.current = remembered;
    form.setFieldValue(path, { ...set, [field]: value });
  };

  return (
    <Box className={classes.row} data-default={set.default || undefined}>
      <Box className={classes.accent} />
      <Box className={classes.columns} p="md">
        <Stack gap="sm" h="100%" className={classes.fields}>
          <TextInput
            label={t(i18nKeys.NAME)}
            size={INPUT_SIZE}
            w="100%"
            maw={300}
            {...form.getInputProps(`${path}.name`)}
          />
          <NumberInput
            size={INPUT_SIZE}
            w={120}
            label={t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.PAYMENT_PERIOD)}
            min={0}
            hideControls
            allowDecimal={false}
            rightSection={
              <Text size={INPUT_SIZE} c="dimmed">
                {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.DAYS_SUFFIX)}
              </Text>
            }
            rightSectionWidth={44}
            rightSectionPointerEvents="none"
            {...form.getInputProps(`${path}.minimumPaymentTermsDelay`)}
          />
          <Switch
            mt="xs"
            size={INPUT_SIZE}
            label={t(i18nKeys.ACTIONS.USE_BELGIAN_LAW)}
            description={t(i18nKeys.ACTIONS.TOOLTIP)}
            {...form.getInputProps(`${path}.useBelgianLaw`, { type: 'checkbox' })}
          />

          <Group gap="xs" grow wrap="nowrap" mt="auto">
            <Button
              variant="light"
              size="compact-sm"
              leftSection={<IconStar size={15} stroke={1.8} />}
              disabled={!canSetAsDefault}
              onClick={onSetAsDefault}
            >
              {t(
                set.default
                  ? i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.IS_DEFAULT
                  : i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.SET_AS_DEFAULT,
              )}
            </Button>
            {/* Parked until deleting a set gets a confirmation step. Restoring it
                also needs the Tooltip and IconTrash imports back.
            <Tooltip
              label={t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.CANNOT_DELETE_SET)}
              disabled={canDelete}
            >
              <Button
                variant="light"
                color="red"
                size="compact-sm"
                leftSection={<IconTrash size={15} stroke={1.8} />}
                data-disabled={!canDelete || undefined}
                onClick={canDelete ? onDelete : undefined}
              >
                {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.DELETE_SET)}
              </Button>
            </Tooltip>
            */}
          </Group>
        </Stack>

        <Divider orientation="vertical" className={classes.feesDivider} />

        <Stack gap="sm" className={classes.fees}>
          <Text tt="uppercase" fw={600} size="xs" c="dimmed">
            {t(i18nKeys.LATE_FEES)}
          </Text>

          <Group gap="sm" wrap="nowrap" align="center">
            <Switch
              size={INPUT_SIZE}
              w={150}
              label={t(i18nKeys.FORM.PREFERENCES.INTEREST)}
              checked={set.interest != null}
              onChange={event => toggleFee('interest', event.currentTarget.checked)}
            />
            {set.interest != null && (
              <>
                <Select
                  size={INPUT_SIZE}
                  w={120}
                  allowDeselect={false}
                  aria-label={t(i18nKeys.FORM.PREFERENCES.PERIODICITY)}
                  data={[
                    { value: 'annual', label: t(i18nKeys.CALCULATION_TYPE.ANNUAL) },
                    { value: 'quarterly', label: t(i18nKeys.CALCULATION_TYPE.QUARTERLY) },
                    { value: 'monthly', label: t(i18nKeys.CALCULATION_TYPE.MONTHLY) },
                  ]}
                  {...form.getInputProps(`${path}.interest.period`)}
                />
                <NumberInput
                  size={INPUT_SIZE}
                  w={90}
                  min={0}
                  hideControls
                  max={100}
                  rightSection={
                    <Text size={INPUT_SIZE} c="dimmed">
                      {'%'}
                    </Text>
                  }
                  rightSectionWidth={24}
                  rightSectionPointerEvents="none"
                  aria-label={t(i18nKeys.FORM.PAYMENT_DELAY.INTEREST_RATE)}
                  {...form.getInputProps(`${path}.interest.value`)}
                />
              </>
            )}
          </Group>

          <Group gap="sm" wrap="nowrap" align="center">
            <Switch
              size={INPUT_SIZE}
              w={150}
              label={t(i18nKeys.FORM.PREFERENCES.FIX_FEE)}
              checked={set.fixFee != null}
              onChange={event => toggleFee('fixFee', event.currentTarget.checked)}
            />
            {set.fixFee != null && (
              <NumberInput
                size={INPUT_SIZE}
                w={90}
                min={0}
                hideControls
                decimalScale={2}
                rightSection={
                  <Text size={INPUT_SIZE} c="dimmed">
                    {currencySymbol}
                  </Text>
                }
                rightSectionWidth={currencySectionWidth}
                rightSectionPointerEvents="none"
                aria-label={t(i18nKeys.FORM.PREFERENCES.AMOUNT_EURO)}
                {...form.getInputProps(`${path}.fixFee.value`)}
              />
            )}
          </Group>

          <Group gap="sm" wrap="nowrap" align="center">
            <Switch
              size={INPUT_SIZE}
              w={150}
              label={t(i18nKeys.PENALTY_CLAUSE)}
              checked={set.penaltyClause != null}
              onChange={event => toggleFee('penaltyClause', event.currentTarget.checked)}
            />
            {set.penaltyClause != null && (
              <>
                {/* The unit, not a separate concept: the value field beside it
                    already renders % or the currency from this very choice. Two symbols in
                    a segmented control say it without a shrinkable select. */}
                <SegmentedControl
                  size={INPUT_SIZE}
                  aria-label={t(i18nKeys.FORM.PREFERENCES.CLAUSE_TYPE)}
                  data={[
                    { value: 'percentage', label: '%' },
                    { value: 'fix', label: currencySymbol },
                  ]}
                  value={isPercentagePenalty ? 'percentage' : 'fix'}
                  onChange={value =>
                    form.setFieldValue(`${path}.penaltyClause.type`, value as 'percentage' | 'fix')
                  }
                />
                {isPercentagePenalty && (
                  <NumberInput
                    size={INPUT_SIZE}
                    w={104}
                    min={0}
                    hideControls
                    decimalScale={2}
                    rightSection={
                      <Text size={INPUT_SIZE} c="dimmed">
                        {currencySymbol}
                      </Text>
                    }
                    rightSectionWidth={currencySectionWidth}
                    rightSectionPointerEvents="none"
                    // Two bare number boxes sit side by side here; the prefix is
                    // what tells them apart without costing a label row.
                    leftSection={
                      <Text size={INPUT_SIZE} c="dimmed">
                        {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.MINIMUM_PREFIX)}
                      </Text>
                    }
                    leftSectionWidth={30}
                    leftSectionPointerEvents="none"
                    aria-label={t(i18nKeys.FORM.PREFERENCES.PENALTY_CLAUSE_MIN, {
                      text: currencySymbol,
                    })}
                    {...form.getInputProps(`${path}.penaltyClause.minimum`)}
                  />
                )}
                <NumberInput
                  size={INPUT_SIZE}
                  w={90}
                  min={0}
                  hideControls
                  max={isPercentagePenalty ? 100 : undefined}
                  decimalScale={2}
                  rightSection={
                    <Text size={INPUT_SIZE} c="dimmed">
                      {isPercentagePenalty ? '%' : currencySymbol}
                    </Text>
                  }
                  rightSectionWidth={isPercentagePenalty ? 24 : currencySectionWidth}
                  rightSectionPointerEvents="none"
                  aria-label={t(i18nKeys.FORM.PREFERENCES.PENALTY_CLAUSE_VALUE, {
                    text: isPercentagePenalty ? '%' : currencySymbol,
                  })}
                  {...form.getInputProps(`${path}.penaltyClause.value`)}
                />
              </>
            )}
          </Group>
        </Stack>

        <Divider orientation="vertical" className={classes.documentDivider} />

        <Box className={classes.document}>
          <TermsAndConditionsDocument
            termsAndConditionsId={set.id}
            hasPDF={set.hasPDF}
            pdfFilename={set.pdfFilename}
            pdfUrl={set.pdfUrl}
          />
        </Box>
      </Box>
    </Box>
  );
};
